// src/pages/admin/AdminBroadcast.jsx
import { useEffect, useState } from "react";
import adminApi from "../../api/adminApi";
import { toast } from "react-toastify";
import { Send, Users, Eye, AlertTriangle, Mail, Loader2, LayoutDashboard, UserPlus, LogIn, Ban } from "lucide-react";

const CTA_OPTIONS = [
  { id: "dashboard", label: "Go to Dashboard", icon: LayoutDashboard },
  { id: "signup",    label: "Create Account",  icon: UserPlus },
  { id: "login",     label: "Login",           icon: LogIn },
  { id: "none",      label: "No Button",       icon: Ban },
];

export default function AdminBroadcast() {
  const [title, setTitle]     = useState("");
  const [tagline, setTagline] = useState("");
  const [body, setBody]       = useState("");
  const [ctaType, setCtaType] = useState("dashboard");
  const [includeFeatureCards, setIncludeFeatureCards] = useState(true);

  const [recipientCount, setCount]  = useState(null);
  const [sending, setSending]       = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [previewHtml, setPreviewHtml] = useState("");
  const [loadingPreview, setLoadingPreview] = useState(false);

  useEffect(() => {
    adminApi.get("/broadcast/recipient-count")
      .then((res) => setCount(res.data.count))
      .catch(() => setCount(null));
  }, []);

  const canSend = title.trim().length > 0 && body.trim().length > 0 && !sending;

  const openPreview = async () => {
    setShowPreview(true);
    setLoadingPreview(true);
    try {
      const res = await adminApi.post("/broadcast/preview-html", {
        title, body, tagline, ctaType, includeFeatureCards,
      });
      if (res.data?.success) setPreviewHtml(res.data.html);
    } catch {
      toast.error("Failed to load preview");
    } finally {
      setLoadingPreview(false);
    }
  };

  const handleSend = async () => {
    setSending(true);
    setShowConfirm(false);
    try {
      const res = await adminApi.post("/broadcast/send", {
        title: title.trim(),
        body: body.trim(),
        tagline: tagline.trim(),
        ctaType,
        includeFeatureCards,
      });
      if (res.data?.success) {
        toast.success(res.data.message, { autoClose: 6000 });
        setTitle("");
        setTagline("");
        setBody("");
      } else {
        throw new Error(res.data?.message || "Failed to send");
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to send broadcast");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="max-w-3xl space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <Mail size={20} className="text-purple-600 dark:text-purple-400" />
            Broadcast Message
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
            Send a polished email to all registered users
          </p>
        </div>
        <div className="flex items-center gap-2 px-3 py-2 bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-lg text-sm font-medium text-purple-700 dark:text-purple-300">
          <Users size={14} />
          {recipientCount === null ? "..." : recipientCount.toLocaleString()} recipients
        </div>
      </div>

      {/* Warning */}
      <div className="flex items-start gap-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl p-4">
        <AlertTriangle size={16} className="text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
        <p className="text-xs text-amber-700 dark:text-amber-400 leading-relaxed">
          This sends a real email to every registered user immediately. There is no undo.
          Preview before sending.
        </p>
      </div>

      {/* Form */}
      <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-5 space-y-5">
        <div>
          <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1.5">
            Title / Subject
          </label>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="e.g. Everything You Need Is Now Available"
            disabled={sending}
            className="w-full px-3 py-2.5 text-sm bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 dark:text-white"
          />
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1.5">
            Tagline <span className="text-gray-400 font-normal">(optional — short line under the title)</span>
          </label>
          <input
            type="text"
            value={tagline}
            onChange={(e) => setTagline(e.target.value)}
            placeholder="e.g. One dashboard. Every digital service."
            disabled={sending}
            className="w-full px-3 py-2.5 text-sm bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 dark:text-white"
          />
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1.5">
            Message Body
          </label>
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder={"Write your message here...\n\nLeave a blank line between paragraphs."}
            rows={7}
            disabled={sending}
            className="w-full px-3 py-2.5 text-sm bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 dark:text-white resize-none"
          />
        </div>

        {/* Feature cards toggle */}
        <label className="flex items-center gap-2.5 cursor-pointer">
          <input
            type="checkbox"
            checked={includeFeatureCards}
            onChange={(e) => setIncludeFeatureCards(e.target.checked)}
            disabled={sending}
            className="w-4 h-4 rounded accent-purple-600"
          />
          <span className="text-sm text-gray-700 dark:text-gray-300">
            Include feature cards (Virtual Numbers, Social Accounts, VPNs, Gifts)
          </span>
        </label>

        {/* CTA button selector */}
        <div>
          <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-2">
            Call-to-action button
          </label>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {CTA_OPTIONS.map((opt) => {
              const Icon = opt.icon;
              const active = ctaType === opt.id;
              return (
                <button
                  key={opt.id}
                  type="button"
                  onClick={() => setCtaType(opt.id)}
                  disabled={sending}
                  className={`flex flex-col items-center gap-1.5 py-3 px-2 rounded-xl border text-xs font-medium transition ${
                    active
                      ? "bg-purple-600 border-purple-600 text-white"
                      : "bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:border-purple-400"
                  }`}
                >
                  <Icon size={16} />
                  {opt.label}
                </button>
              );
            })}
          </div>
        </div>

        <div className="flex items-center gap-3 pt-2">
          <button
            onClick={openPreview}
            disabled={!title.trim() && !body.trim()}
            className="flex items-center gap-2 px-4 py-2.5 text-sm font-medium text-gray-700 dark:text-gray-200 bg-gray-100 dark:bg-gray-800 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition disabled:opacity-50"
          >
            <Eye size={15} /> Preview
          </button>
          <button
            onClick={() => setShowConfirm(true)}
            disabled={!canSend}
            className="flex items-center gap-2 px-5 py-2.5 text-sm font-semibold text-white bg-purple-600 hover:bg-purple-700 disabled:bg-purple-300 rounded-lg transition ml-auto"
          >
            {sending ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />}
            {sending ? "Sending..." : "Send Broadcast"}
          </button>
        </div>
      </div>

      {/* Preview Modal — renders the REAL server-generated HTML */}
      {showPreview && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 px-4" onClick={() => setShowPreview(false)}>
          <div
            className="bg-gray-100 dark:bg-gray-950 rounded-xl max-w-xl w-full max-h-[85vh] overflow-y-auto shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-4 bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-800 flex items-center justify-between sticky top-0 z-10">
              <p className="text-sm font-semibold text-gray-900 dark:text-white">Email Preview</p>
              <button onClick={() => setShowPreview(false)} className="text-gray-400 hover:text-gray-600 text-sm">Close</button>
            </div>
            <div className="p-6">
              {loadingPreview ? (
                <div className="flex items-center justify-center py-16">
                  <Loader2 size={22} className="animate-spin text-purple-500" />
                </div>
              ) : (
                <div dangerouslySetInnerHTML={{ __html: previewHtml }} />
              )}
            </div>
          </div>
        </div>
      )}

      {/* Confirm Modal */}
      {showConfirm && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 px-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl p-5 max-w-sm w-full text-center shadow-2xl">
            <AlertTriangle size={28} className="mx-auto text-amber-500 mb-3" />
            <h2 className="text-lg font-bold text-gray-900 dark:text-white mb-2">
              Send to {recipientCount?.toLocaleString() || "all"} users?
            </h2>
            <p className="text-sm text-gray-600 dark:text-gray-300 mb-5">
              This will email every registered user right now. This cannot be undone.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowConfirm(false)}
                className="flex-1 py-2.5 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg text-sm font-medium transition"
              >
                Cancel
              </button>
              <button
                onClick={handleSend}
                className="flex-1 py-2.5 bg-purple-600 hover:bg-purple-700 text-white rounded-lg text-sm font-medium transition"
              >
                Yes, Send
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}